import { apiFetch } from './client';

export interface WaNumber {
  id: string;
  displayPhone: string | null;
  waPhoneNumberId: string;
  businessAccountId: string | null;
  status: string;
}

export interface Branch {
  id: string;
  name: string;
  location: string | null;
  createdAt: string;
  numbers: WaNumber[];
  stickyAssignment?: boolean;
  stickyTimeoutMinutes?: number | null;
}

export interface BranchListResponse {
  branches: Branch[];
}

export function listBranches(): Promise<BranchListResponse> {
  return apiFetch<BranchListResponse>('/branches');
}

export function updateStickyAssignment(
  branchId: string,
  input: { stickyAssignment: boolean; stickyTimeoutMinutes: number | null },
): Promise<{ stickyAssignment: boolean; stickyTimeoutMinutes: number | null }> {
  return apiFetch(`/branches/${branchId}/sticky-assignment`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function createBranch(input: { name: string; location?: string | null }): Promise<Branch> {
  return apiFetch<Branch>('/branches', { method: 'POST', body: JSON.stringify(input) });
}

export interface WaConnectionInput {
  waPhoneNumberId: string;
  /** Omit (or leave blank) to keep the currently-stored token unchanged. */
  accessToken?: string;
  businessAccountId?: string | null;
}

export interface WaConnectionResult {
  branchId: string;
  waPhoneNumberId: string;
  businessAccountId: string | null;
  connected: boolean;
}

export function connectWaNumber(branchId: string, input: WaConnectionInput): Promise<WaConnectionResult> {
  return apiFetch<WaConnectionResult>(`/branches/${branchId}/wa-connection`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

/** Remove a number's connection entirely (hard-delete). Frees the phone number
 *  id for reconnection. Admin only. */
export function deleteWaNumber(branchId: string, waPhoneNumberId: string): Promise<{ deleted: boolean }> {
  return apiFetch<{ deleted: boolean }>(
    `/branches/${branchId}/wa-connection/${encodeURIComponent(waPhoneNumberId)}`,
    { method: 'DELETE' },
  );
}

/** Delete a whole branch (admin only). Its connected numbers cascade away.
 *  A branch with conversation history is protected — the API returns 409
 *  { error: 'branch_has_activity' }. */
export function deleteBranch(branchId: string): Promise<{ deleted: boolean }> {
  return apiFetch<{ deleted: boolean }>(`/branches/${branchId}`, { method: 'DELETE' });
}
